/**
 * Cliente HTTP tipado para `/api`. Único ponto de contato com o backend —
 * nenhuma outra parte do frontend chama `fetch` diretamente.
 *
 * Envelope de erro do contrato: `{ error: { code, message, fields? } }`.
 * Toda falha (rede, parse ou HTTP não-2xx) vira uma `ApiError`.
 */
import { QueryClient } from '@tanstack/react-query';
import type { ApiErrorBody } from './types';

const BASE_URL = '/api';

/** Erro de API com o código estável e a mensagem em pt-BR prontos para exibir. */
export class ApiError extends Error {
  status: number;
  code: string;
  fields?: Record<string, string>;

  constructor(status: number, code: string, message: string, fields?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Registra o que fazer quando o servidor responde 401 (sessão ausente ou expirada).
 * `useAuth` chama isto para limpar o contexto e mandar a pessoa para `/entrar`
 * sem recarregar a página. Retorna uma função para desregistrar.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

interface ErrorEnvelope {
  error?: ApiErrorBody;
}

/**
 * `suppressUnauthorized`: não dispara o handler global de 401. Use só para as
 * chamadas que servem para *checar* se há sessão (`GET /auth/me` no boot,
 * `POST /auth/login`, `POST /auth/register`) — para uma pessoa anônima, um 401
 * delas é o resultado normal, não uma sessão que expirou no meio do uso. Para
 * todo o resto, o 401 significa sessão expirada e deve mandar para `/entrar`.
 */
export interface RequestOptions {
  suppressUnauthorized?: boolean;
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return null;
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function request<T>(path: string, init: RequestInit = {}, options: RequestOptions = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(0, 'sem_conexao', 'Não foi possível conectar ao servidor. Verifique sua conexão.');
  }

  const body = await parseBody(res);

  if (!res.ok) {
    if (res.status === 401 && !options.suppressUnauthorized) unauthorizedHandler?.();
    const envelope = (body ?? {}) as ErrorEnvelope;
    throw new ApiError(
      res.status,
      envelope.error?.code ?? 'erro_desconhecido',
      envelope.error?.message ?? 'Algo deu errado. Tente de novo em instantes.',
      envelope.error?.fields
    );
  }

  return body as T;
}

/** Monta a URL absoluta de uma rota da API (uso em links diretos: PDF, impressão). */
export const apiUrl = (path: string): string => `${BASE_URL}${path}`;

export const apiFetch = request;

export function get<T>(path: string, options?: RequestOptions): Promise<T> {
  return request<T>(path, { method: 'GET' }, options);
}

export function post<T>(path: string, data?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(
    path,
    {
      method: 'POST',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    },
    options
  );
}

export function patch<T>(path: string, data?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(
    path,
    {
      method: 'PATCH',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    },
    options
  );
}

export function put<T>(path: string, data?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(
    path,
    {
      method: 'PUT',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    },
    options
  );
}

export function del<T>(path: string, options?: RequestOptions): Promise<T> {
  return request<T>(path, { method: 'DELETE' }, options);
}

/** Fábrica do QueryClient com os padrões do projeto. Uma instância por app (ver `main.tsx`). */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
    },
  });
}
